import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onHasUnsavedChanges } from '../../store/helpers/helpersSlice';
import ConfirmationModal from '../modal/ConfirmationModal';
import { FormattedMessage } from 'react-intl';
import { useBlocker } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/react';

const defaultProps = {
    header: <FormattedMessage id="common_discard_changes" />,
    content: <FormattedMessage id="common_confirmation_explanation" />,
};

/**
 * Component that wraps unsaved changes prompt before navigating away from a page
 *
 * @param when - flag indicating when to activate the prompt on navigation change
 * @param header - confirmation modal header
 * @param content - confirmation modal content
 * @param onPromptConfirm - callback function that executes after confirming of the discard changes ( Used in all basis items )
 */
const PromptWrapper = ({ when, header = defaultProps.header, content = defaultProps.content, onPromptConfirm }) => {
    const modal = useDisclosure();
    const dispatch = useDispatch();
    const hasUnsavedChanges = useSelector((state) => state.helpers.hasUnsavedChanges);
    const condition = useMemo(() => (when === undefined ? hasUnsavedChanges : when), [when, hasUnsavedChanges]);

    let blocker = useBlocker(
        ({ currentLocation, nextLocation }) => condition && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ''; // Chrome requires `event.returnValue` to be set
        };

        if (condition) {
            window.addEventListener('beforeunload', onBeforeUnload);
        }

        return () => {
            if (condition) {
                window.removeEventListener('beforeunload', onBeforeUnload);
            }
        };
    }, [condition]);

    const onCancel = () => {
        blocker.reset();
        modal.onClose();
    };

    const confirmNavigation = () => {
        if (onPromptConfirm) {
            onPromptConfirm();
        }
        modal.onClose();
        blocker.proceed();
        dispatch(onHasUnsavedChanges(false));
    };

    useEffect(() => {
        if (blocker.state === 'blocked' && condition) {
            modal.onOpen();
        }
    }, [blocker.state, condition, modal]);

    return (
        <>
            {modal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={onCancel}
                    onConfirm={confirmNavigation}
                    hasExtraStep
                    header={header}
                    content={content}
                />
            )}
        </>
    );
};

export default PromptWrapper;
